import PropTypes from 'prop-types';
import React, { Component } from 'react';
import classNames from 'classnames/bind';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import ModalBox from 'yoda-core-components/lib/components/ModalBox/ModalBox';
import Button from 'yoda-core-components/lib/components/Button';
import SelectDropdown from 'yoda-site-components/lib/components/SelectDropdown/SelectDropdown';
import Messages, {
    messagesLevels,
    messagesActions as MessagesActions,
} from 'yoda-site-components/lib/components/Messages';
import * as styles from './BundleQuantity.css';
import ProductConstants from '../../../helpers/ProductConstants';
import { setSelectedBundleQuantity } from '../../../actions/BundleQuantityAction';
import getBundleInventoryStatus, {
    getInventoryForQuantityDropdown,
} from '../../../selectors/Bundle/BundleInventorySelector';
import { isPromotionalBundle as isPromotionalBundleSelector } from '../../../selectors/Bundle/BundleCommonSelector';
import { formErrorAnalytics } from '../../../actions/AnalyticsAction';
import bundleQuantityModalAction from '../../../actions/BundleQuantityModalAction';

const cx = classNames.bind(styles);

export class Quantity extends Component {
    static propTypes = {
        productDetails: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
        productOptions: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
        actions: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
        deviceType: PropTypes.oneOfType([PropTypes.object]),
        automationId: PropTypes.string,
        bundleQuantity: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
        messages: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
        bundleInventoryStatus: PropTypes.oneOfType([PropTypes.object]),
        isPromotionalBundle: PropTypes.bool,
        messagesTexts: PropTypes.oneOfType([PropTypes.object]),
        bundleQuantityModal: PropTypes.oneOfType([PropTypes.object]),
        getItemInventoryStatus: PropTypes.bool,
    };

    static defaultProps = {
        automationId: '',
        bundleQuantity: 0,
        actions: {},
        query: {},
        deviceType: {},
        productOptions: {},
        bundleInventoryStatus: {},
        messages: [],
        isPromotionalBundle: false,
        messagesTexts: {},
        bundleQuantityModal: {},
        getItemInventoryStatus: false,
    };

    constructor() {
        super();
        this.selectedPPID = '';
        this.triggerFormErrorAnalytics = true;
    }

    componentDidMount() {
        const { productDetails } = this.props;
        const quantity = {};
        quantity[productDetails.id] = 0;
        this.props.actions.setSelectedBundleQuantity(quantity);
        this.props.actions.bundleQuantityModal({ [productDetails.id]: false });
    }

    componentWillReceiveProps(nextProps) {
        const {
            bundleInventoryStatus: {
                inventoryStatus: nextInventoryStatus = {},
                isEveryInventoryAvailable: nextIsEveryInventoryAvailable,
            },
            bundleQuantity: nextBundleQuantity,
            getItemInventoryStatus: nextGetItemInventoryStatus,
        } = nextProps;
        const {
            actions,
            bundleInventoryStatus: { inventoryStatus = {}, isEveryInventoryAvailable },
            bundleQuantity,
            productDetails: { dimensions = [] },
            getItemInventoryStatus,
        } = this.props;

        /* istanbul ignore next */
        if (
            this.selectedPPID &&
            nextInventoryStatus[this.selectedPPID] &&
            inventoryStatus[this.selectedPPID] &&
            nextInventoryStatus[this.selectedPPID].isInventoryAvailable &&
            inventoryStatus[this.selectedPPID].isInventoryAvailable !==
                nextInventoryStatus[this.selectedPPID].isInventoryAvailable
        ) {
            actions.removeAllMessages({
                section: `INVENTORY_UNAVAILABLE_MESSAGES_SECTION_${this.selectedPPID}`,
            });
            this.selectedPPID = '';
        }

        /* istanbul ignore else */
        if (
            isEveryInventoryAvailable !== nextIsEveryInventoryAvailable &&
            nextIsEveryInventoryAvailable
        ) {
            actions.removeAllMessages({ section: 'ADD_TO_CART_MESSAGES_ID' });
        }

        if (nextBundleQuantity[this.selectedPPID] !== bundleQuantity[this.selectedPPID]) {
            /* istanbul ignore next */
            dimensions.forEach(({ id: dimensionId }) => {
                actions.removeAllMessages({
                    section: `${dimensionId}_MESSAGES_SECTION_${this.selectedPPID}`,
                });
                actions.removeMessage({ section: dimensionId, id: `${dimensionId}MessageId` });
                actions.removeAllMessages({ section: `OOS_${dimensionId}` });
            });
        }
        if (
            getItemInventoryStatus !== nextGetItemInventoryStatus &&
            nextGetItemInventoryStatus === false
        ) {
            this.setSelectedQuantity(0);
        }
    }

    shouldComponentUpdate({
        productOptions,
        productDetails,
        bundleQuantity,
        messages,
        bundleQuantityModal,
        getItemInventoryStatus,
    }) {
        return (
            !isEqual(this.props.productOptions, productOptions) ||
            !isEqual(this.props.productDetails, productDetails) ||
            !isEqual(this.props.bundleQuantity, bundleQuantity) ||
            !isEqual(this.props.messages, messages) ||
            !isEqual(this.props.bundleQuantityModal, bundleQuantityModal) ||
            JSON.stringify(getItemInventoryStatus) !==
                JSON.stringify(this.props.getItemInventoryStatus)
        );
    }

    getInitialQuantity(selectedQuantity) {
        const {
            productDetails: { dimensions, id },
            getItemInventoryStatus,
        } = this.props;
        let initialQuantity;
        /* istanbul ignore next */
        if (!getItemInventoryStatus) {
            /**
             * Trigger form error analytics call only for OOS item having no dimensions (ex: items like Accessories).
             * Added triggerFormErrorAnalytics flag to prevent duplicate calls when this component re-renders on page load.
             * MPDPYODA-4379
             */
            if (isEmpty(dimensions) && this.triggerFormErrorAnalytics) {
                const {
                    actions,
                    messagesTexts: { ProductOptionsMessages },
                } = this.props;
                this.triggerFormErrorAnalytics = false;
                actions.addMessage({
                    section: `OOS_${id}`,
                    message: {
                        id: `OOS_MessageId_${id}`,
                        title: 'Out of stock',
                        type: 'warning',
                    },
                });
                actions.formErrorAnalytics([
                    {
                        errorDescription: ProductOptionsMessages.oosMessage.toLowerCase(),
                    },
                ]);
            }
            initialQuantity = 0;
        } else if (isNil(selectedQuantity)) {
            initialQuantity = 0;
        } else {
            initialQuantity = selectedQuantity;
        }

        this.isBundleQuantityChanged(selectedQuantity) && this.mergeQuantity(initialQuantity);

        return initialQuantity;
    }

    setSelectedQuantity = (value) => {
        const { actions, isPromotionalBundle } = this.props;
        /* istanbul ignore next */
        if (parseInt(value, 10) === 0) {
            if (isPromotionalBundle) {
                this.openModal();
            } else {
                this.changeQuantity(0)();
            }
        }
        this.isBundleQuantityChanged(value) && this.mergeQuantity(value);
    };

    isBundleQuantityChanged = (quantity) => {
        const {
            bundleQuantity,
            productDetails: { id },
        } = this.props;

        return bundleQuantity[id] !== quantity;
    };

    mergeQuantity(value) {
        const { productDetails } = this.props;
        const quantity = {};
        quantity[productDetails.id] = parseInt(value, 10);
        this.props.actions.setSelectedBundleQuantity(quantity);
        this.selectedPPID = productDetails.id;
    }

    changeQuantity = (value) => () => {
        this.triggerFormErrorAnalytics = true;
        this.getInitialQuantity(value);
        this.closeModal();
    };

    openModal() {
        this.props.actions.bundleQuantityModal({ [this.selectedPPID]: true });
    }

    closeModal = () => {
        this.props.actions.bundleQuantityModal({ [this.selectedPPID]: false });
    };

    quantityChangeConfirm = () => (
        <div className={cx('changeQuantityContainer')}>
            <h2>Are you sure you want to remove this item?</h2>
            <p>Save more when you buy all items in this package deal.</p>
            <div data-automation-id="bundle-quantity-block">
                <Button
                    buttonType="Secondary"
                    automationId="go-back"
                    className={cx('selectOptionBtn', 'go')}
                    onClick={this.changeQuantity(1)}
                    data-attribute={1}
                >
                    <span className={cx('btnText')}>Go Back</span>
                </Button>
                <Button
                    buttonType="Primary"
                    automationId="continue"
                    className={cx('selectOptionBtn', 'continue')}
                    onClick={this.changeQuantity(0)}
                    data-attribute={0}
                >
                    <span className={cx('btnText')}>Continue</span>
                </Button>
            </div>
        </div>
    );

    render() {
        const {
            productDetails,
            bundleQuantity,
            productOptions,
            bundleQuantityModal,
            getItemInventoryStatus,
        } = this.props;
        const defaultValueRequired = false;
        const displayQuantityCount = get(productOptions, 'selectedProductLot[0].maxQuantity', 0);
        const quantityData = [...Array(displayQuantityCount + 1)].map((_, index) => ({
            id: index,
            value: index,
        }));

        return (
            <div className={cx('wrapper')}>
                <div className={dt(['text-sm', 'text-black', 'font-semibold', 'pb-2'])}>
                    {ProductConstants.QUANTITY}
                </div>
                <SelectDropdown
                    name="quantity"
                    datasource={quantityData}
                    defaultValueRequired={defaultValueRequired}
                    automationId={this.props.automationId}
                    optionDisplayValue="value"
                    optionValue="id"
                    disabled={!getItemInventoryStatus}
                    onChangeHandler={this.setSelectedQuantity}
                    value={this.getInitialQuantity(bundleQuantity[productDetails.id])}
                />
                <Messages
                    id={`OOS_${productDetails.id}`}
                    level={messagesLevels.inline}
                    messages={this.props.messages[`OOS_${productDetails.id}`]}
                />
                <Messages
                    id={`INVENTORY_UNAVAILABLE_MESSAGES_SECTION_${productDetails.id}`}
                    level={messagesLevels.inline}
                    messages={
                        this.props.messages[
                            `INVENTORY_UNAVAILABLE_MESSAGES_SECTION_${productDetails.id}`
                        ]
                    }
                />
                <ModalBox
                    showModal={bundleQuantityModal[productDetails.id]}
                    onClose={this.changeQuantity(1)}
                    defaultCloseBtn={!this.props.deviceType.isMobile}
                    modalTheme={styles.bundleModalWrapper}
                    defaultHeader
                    automationId="protection-modal"
                    modalBlockTheme={cx('customModalBlock')}
                >
                    {this.quantityChangeConfirm()}
                </ModalBox>
            </div>
        );
    }
}

const mapStateToProps = (state, ownProps) => ({
    bundleQuantity: state.bundleQuantity,
    bundleInventoryStatus: getBundleInventoryStatus(state),
    isPromotionalBundle: isPromotionalBundleSelector(state),
    messagesTexts: get(state, 'context.messagesTexts'),
    bundleQuantityModal: state.bundleQuantityModal,
    getItemInventoryStatus: getInventoryForQuantityDropdown(ownProps?.productDetails?.id || '')(
        state
    ),
});

const mapDispatchToProps = (dispatch) => ({
    actions: bindActionCreators(
        {
            setSelectedBundleQuantity,
            ...MessagesActions,
            formErrorAnalytics,
            bundleQuantityModal: bundleQuantityModalAction,
        },
        dispatch
    ),
});

export default connect(mapStateToProps, mapDispatchToProps)(Quantity);
